import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@client/lib/utils"

const buttonVariants = cva(
  // hover-elevate / active-elevate replace per-variant hover colours: one overlay that works on any
  // background, so every variant responds identically instead of each inventing its own shade.
  // Design pass, step 0: pills and the public site's soft focus halo (4px, translucent turquesa)
  // instead of a hard 2px ring with an offset.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold" +
    " transition-[color,background-color,box-shadow] focus-visible:outline-none focus-visible:ring-4" +
    " focus-visible:ring-primary/25" +
    " disabled:pointer-events-none disabled:opacity-50" +
    " [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0" +
    " hover-elevate active-elevate-2",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-[0_6px_16px_-8px_rgb(0_217_188/0.7)]",
        // The landing page's call-to-action gradient. One per screen, for the action the screen is
        // for; everything else stays `default` or quieter, or the gradient stops meaning anything.
        brand: "bg-[linear-gradient(to_right,#47f6d8,#00d9bc)] text-[#00382f] shadow-[0_10px_24px_-10px_rgb(0_217_188/0.7)]",
        destructive: "bg-destructive text-destructive-foreground shadow-sm",
        outline: "border border-border bg-card shadow-[0_1px_2px_rgb(15_27_36/0.05)]",
        secondary: "bg-secondary text-secondary-foreground",
        ghost: "border border-transparent",
        // A link is text, not a surface: the overlay would tint a rectangle around it.
        link: "text-primary underline-offset-4 hover:underline no-default-elevate",
      },
      size: {
        // Min rather than fixed heights, and 44px is the WCAG 2.5.5 AAA touch floor (PE's rule).
        // `sm` sits below it deliberately and is reserved for dense desktop contexts.
        default: "min-h-11 px-4 py-2",
        sm: "min-h-8 px-3.5 text-xs",
        lg: "min-h-12 px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
